import { PieArcDatum } from 'd3';
import { isEqual } from 'lodash';
import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { isRequiredFeatureEnabled } from 'src/components/auth';
import {
  LocalFilterOptions,
  ResetFilterOptions,
  SaveFilterParams,
} from 'src/components/organisms';
import {
  TimeframeInput,
  useGetIssueCategoriesByDepartmentGraphQuery,
} from 'src/graphql';
import * as Types from 'src/types';
import { InsightsDashboard, State } from 'src/types';
import {
  getOrderedColor,
  sortDataArrayByNumber,
  sortDataArrayByPercentage,
  sortDataArrayByString,
} from 'src/utils';
import { CHART_IDS, trackChartRedrawn } from 'src/utils/analytics';
import { buildLocationQueryVariables } from 'src/utils/insights/buildLocationQueryVariables';
import { getYear } from 'src/utils/time';
import { getColumnNames } from '../../EthicsOperationsDataview/helpers';
import {
  ColumnSortType,
  TableDataType,
} from '../../EthicsOperationsDataview/hooks';
import { flattenDonutAndBarGraphData } from '../../EthicsOperationsDataview/utils/flattenDonutAndBarGraphData';

interface Parameters {
  globalFilterSites: string[];
  globalFilterCountries: string[];
  selectedCaseCountryCodes: string[];
  selectedCaseRegionIds: string[];
  globalFilterTimeframe: TimeframeInput;
  organisationData: {
    sites?: string[];
    countries?: string[];
    departments?: string[];
    oldestReportSubmittedAtDate: Date;
    categories?: string[];
  };
}

const departmentDisplayLimit = 20;

export const useIssueCategoriesByDepartment = ({
  globalFilterSites,
  globalFilterCountries,
  selectedCaseCountryCodes,
  selectedCaseRegionIds,
  globalFilterTimeframe,
  organisationData,
}: Parameters) => {
  const storedGlobalFilters = useSelector(
    (state: State) =>
      state.insightsGlobalFilters[InsightsDashboard.ORGANISATIONAL],
  );
  const currentGlobalLocationFilter =
    storedGlobalFilters.currentGlobalLocationFilter;
  const [selectedDepartments, setSelectedDepartments] = useState(
    organisationData.departments ?? [],
  );
  const [selectedCategories, setSelectedCategories] = useState(
    organisationData.categories ?? [],
  );
  const [selectedSites, setSelectedSites] = useState(globalFilterSites);
  const [selectedCountries, setSelectedCountries] = useState(
    globalFilterCountries,
  );
  const [localLocationFilter, setLocalLocationFilter] = useState(
    currentGlobalLocationFilter,
  );
  const [timeframe, setTimeframe] = useState(globalFilterTimeframe);
  const [selectedSegment, setSelectedSegment] = useState({
    department: '',
    color: '',
  });
  const [displayTableData, setDisplayTableData] = useState<TableDataType[]>([]);
  const [storedTableData, setStoredTableData] = useState<TableDataType[]>([]);
  const [sortedColumn, setSortedColumn] = useState<ColumnSortType>({
    columnName: 'Department name',
    direction: 'asc',
  });
  const [categoryAxisLabelCount, setCategoryAxisLabelCount] =
    useState<number>(0);
  const [pdfIsDownloading, setPdfIsDownloading] = useState<boolean>(false);
  const localQueryLocationVariables = buildLocationQueryVariables({
    locationFilter: localLocationFilter,
    locationFilterCategory: storedGlobalFilters.locationFilterCategory,
    sites: selectedSites,
    countries: selectedCountries,
    caseCountryCodes: selectedCaseCountryCodes,
    caseRegionIds: selectedCaseRegionIds,
  });
  const globalQueryLocationVariables = buildLocationQueryVariables({
    locationFilter: currentGlobalLocationFilter,
    locationFilterCategory: storedGlobalFilters.locationFilterCategory,
    sites: globalFilterSites,
    countries: globalFilterCountries,
    caseCountryCodes: selectedCaseCountryCodes,
    caseRegionIds: selectedCaseRegionIds,
  });

  const { data, isLoading } = useGetIssueCategoriesByDepartmentGraphQuery({
    timeframe,
    ...localQueryLocationVariables,
    departments: selectedDepartments,
    categories: selectedCategories,
  });
  const { data: resetListData } = useGetIssueCategoriesByDepartmentGraphQuery({
    timeframe: globalFilterTimeframe,
    ...globalQueryLocationVariables,
  });
  const graphData = data?.getIssueCategoriesByDepartmentGraph;
  const barChartData = data?.getIssueCategoriesByDepartmentGraph.data.find(
    (d) => d.donutSegment === selectedSegment.department,
  );
  const organisation = useSelector((state: Types.State) => state.organisation);
  const features = useSelector((state: Types.State) => state.features);
  const { topDepartments } = useSelector(
    (state: Types.State) => state.insightsGlobalFilters.organisational,
  );
  const dispatch = useDispatch();
  const segmentEnabled = isRequiredFeatureEnabled(
    features,
    Types.FeatureFlag.FRONTEND_SEGMENT_TRACKING,
  );
  const firstDepartment = graphData?.data[0].donutSegment ?? '';

  useEffect(() => {
    if (topDepartments) {
      setSelectedDepartments(topDepartments);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (firstDepartment) {
      setSelectedSegment({
        department: firstDepartment,
        color: getOrderedColor(0),
      });
    } else {
      setSelectedSegment({
        department: '',
        color: '',
      });
    }
  }, [firstDepartment]);

  useEffect(() => {
    if (resetListData?.getIssueCategoriesByDepartmentGraph.data.length) {
      const newResetList =
        resetListData.getIssueCategoriesByDepartmentGraph.data.map(
          (dep) => dep.donutSegment,
        );
      dispatch({
        type: Types.InsightsGlobalFilterTypes.UPDATE_TOP_DEPARTMENTS,
        payload: newResetList,
      });
      setSelectedDepartments(newResetList);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resetListData]);

  useEffect(() => {
    setSelectedSites(globalFilterSites);
    setLocalLocationFilter(currentGlobalLocationFilter);
  }, [globalFilterSites, currentGlobalLocationFilter]);

  useEffect(() => {
    setSelectedCountries(globalFilterCountries);
    setLocalLocationFilter(currentGlobalLocationFilter);
  }, [globalFilterCountries, currentGlobalLocationFilter]);

  useEffect(() => {
    setTimeframe(globalFilterTimeframe);
  }, [globalFilterTimeframe]);

  useEffect(() => {
    if (!selectedCategories.length && organisationData.categories?.length) {
      setSelectedCategories(organisationData.categories);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organisationData.categories]);

  const localFilterOptions: LocalFilterOptions = {
    site: {
      options: organisationData.sites ?? [],
      selectedOptions: selectedSites,
    },
    country: {
      options: organisationData.countries ?? [],
      selectedOptions: selectedCountries,
    },
    time: {
      timeframe,
      oldestSelectableYear: getYear(
        organisationData.oldestReportSubmittedAtDate,
      ),
    },
    department: {
      options: organisationData.departments ?? [],
      selectedOptions: selectedDepartments,
      limit: departmentDisplayLimit,
      selectAllOptions: topDepartments,
    },
    category: {
      options: organisationData.categories ?? [],
      selectedOptions: selectedCategories,
    },
    selectedLocationFilter: localLocationFilter,
  };

  const resetLocalFilters: ResetFilterOptions = {
    siteOptions: globalFilterSites,
    countryOptions: globalFilterCountries,
    departmentOptions: topDepartments,
    timeframe: globalFilterTimeframe,
    categoryOptions: organisationData.categories ?? [],
    globalFilterLocation: currentGlobalLocationFilter,
  };

  const saveLocalFilters = (params: SaveFilterParams) => {
    if (params.selectedSites) {
      setSelectedSites(params.selectedSites);
    }
    if (params.selectedCountries) {
      setSelectedCountries(params.selectedCountries);
    }
    if (params.selectedDepartments) {
      setSelectedDepartments(params.selectedDepartments);
    }
    if (params.selectedTimeframe) {
      setTimeframe(params.selectedTimeframe);
    }
    if (params.selectedCategories) {
      setSelectedCategories(params.selectedCategories);
    }
    if (params.selectedLocationFilter) {
      setLocalLocationFilter(params.selectedLocationFilter);
    }
  };

  const formattedDonutData = useMemo(
    () =>
      graphData?.data.reduce(
        (obj, item) => ({
          ...obj,
          [item.donutSegment]: item.total,
        }),
        {} as Record<string, number>,
      ),
    [graphData],
  );

  // eslint-disable-next-line react-hooks/exhaustive-deps
  let issueCategoriesByDepartmentList: string[] = [];

  if (formattedDonutData) {
    const keyValuePairs: [string, number][] =
      Object.entries(formattedDonutData);
    keyValuePairs.sort((a, b) => b[1] - a[1]);
    issueCategoriesByDepartmentList = keyValuePairs.map((pair) => pair[0]);
  }

  const departmentColors = useMemo(
    () =>
      issueCategoriesByDepartmentList.map((_, index) => getOrderedColor(index)),
    [issueCategoriesByDepartmentList],
  );

  const issueCategoriesByDepartmentLegend = issueCategoriesByDepartmentList.map(
    (item, index) => {
      return {
        color: departmentColors[index],
        text: item,
        active: item === selectedSegment?.department,
      };
    },
  );

  const formattedBarChartData = barChartData?.categories
    .map((c, i) => ({
      bar: i,
      label: c.category,
      value: c.total,
    }))
    .reverse();

  const onDonutClick = (d: PieArcDatum<[string, number]>) => {
    if (organisation.organisation && segmentEnabled) {
      trackChartRedrawn(
        organisation.organisation.id,
        'organisational',
        CHART_IDS.categories_per_department,
      );
    }
    setSelectedSegment({
      department: d.data[0],
      color: getOrderedColor(d.index),
    });
  };

  const onLegendClick = (option: string) => {
    const department = issueCategoriesByDepartmentLegend.find(
      (item) => item.text === option,
    );
    if (department) {
      if (organisation.organisation && segmentEnabled) {
        trackChartRedrawn(
          organisation.organisation.id,
          'organisational',
          CHART_IDS.categories_per_department,
        );
      }
      setSelectedSegment({
        department: option,
        color: department.color,
      });
    }
  };

  const tableColumns = [
    { label: 'Department name' },
    { label: 'Issue category name' },
    { label: 'Issue category count' },
    { label: 'Department percentage' },
  ];

  useEffect(() => {
    const tableData = flattenDonutAndBarGraphData(
      graphData?.data ?? [],
      'department',
    );

    if (!isEqual(tableData, storedTableData)) {
      setStoredTableData(tableData);
    }
    const labelsCount = graphData?.data[0].categories.length ?? 0;
    setCategoryAxisLabelCount(labelsCount);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [graphData]);

  useEffect(() => {
    handleColumnSort(sortedColumn.columnName, sortedColumn.direction);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storedTableData]);

  const handleColumnSort = (columnName: string, direction: 'asc' | 'desc') => {
    const columnIndex = getColumnNames(tableColumns).indexOf(columnName);
    setSortedColumn({ columnName, direction });
    switch (columnName) {
      case 'Department name':
      case 'Issue category name': {
        const newTableData = sortDataArrayByString(
          storedTableData,
          columnIndex,
          direction,
        );
        setDisplayTableData(newTableData);
        break;
      }
      case 'Issue category count': {
        const newTableData = sortDataArrayByNumber(
          storedTableData,
          columnIndex,
          direction,
        );
        setDisplayTableData(newTableData);
        break;
      }
      case 'Department percentage': {
        const newTableData = sortDataArrayByPercentage(
          storedTableData,
          columnIndex,
          direction,
        );
        setDisplayTableData(newTableData);
        break;
      }
      default: {
        setDisplayTableData(storedTableData);
        break;
      }
    }
  };

  return {
    centerLabel: graphData?.centerText ?? '',
    donutData: formattedDonutData,
    donutLegend: issueCategoriesByDepartmentLegend,
    onDonutClick,
    onLegendClick,
    selectedSegment: selectedSegment.department,
    xTitle: graphData?.xTitle ?? '',
    yTitle: graphData?.yTitle ?? '',
    barData: formattedBarChartData,
    barColor: selectedSegment.color,
    xAxisMax: graphData?.maxCategoryTotal,
    localFilterOptions,
    saveLocalFilters,
    dataIsLoading: isLoading,
    resetLocalFilters,
    categoriesByDepartment: graphData?.data,
    tableColumns,
    tableData: displayTableData,
    handleColumnSort,
    sortedColumn,
    categoryAxisLabelCount,
    pdfIsDownloading,
    setPdfIsDownloading,
    localLocationFilter,
  };
};
